"use client";

import { trackPopupClick, trackPopupDisplay } from "@/lib/analytics/ga-events";
import { trackOpenAIFormLead } from "@/lib/analytics/openai-events";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

const ScrollNewsletterPopup = () => {
  const t = useTranslations("Articles.popups");
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [hasScrolledToHalf, setHasScrolledToHalf] = useState(false);
  const hasTrackedDisplay = useRef(false);

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  useEffect(() => {
    const handleScroll = () => {
      if (hasScrolledToHalf || isVisible) return;

      const articleContent = document.querySelector(".inter.mt-8");
      if (!articleContent) return;

      const articleHeight = articleContent.scrollHeight;
      const articleTop = articleContent.getBoundingClientRect().top + window.scrollY;
      const scrolled = window.scrollY;
      const scrolledInArticle = scrolled - articleTop;
      const percentScrolled = (scrolledInArticle / articleHeight) * 100;

      if (percentScrolled >= 50) {
        setHasScrolledToHalf(true);
        setIsVisible(true);

        if (!hasTrackedDisplay.current) {
          trackPopupDisplay("newsletter_scroll");
          hasTrackedDisplay.current = true;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isVisible, hasScrolledToHalf]);

  useEffect(() => {
    if (isVisible) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isVisible]);

  const handleClose = () => {
    trackPopupClick("newsletter_scroll", "close");
    setIsVisible(false);

    setEmail("");
    setSubmitStatus({ type: null, message: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitStatus({ type: null, message: "" });
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to subscribe");
      }

      trackPopupClick("newsletter_scroll", "newsletter_submit");
      trackOpenAIFormLead("newsletter");

      setSubmitStatus({
        type: "success",
        message: data.message || "Successfully subscribed!",
      });

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);

      // Show error message
      setSubmitStatus({
        type: "error",
        message: error.message || "Failed to subscribe. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleClose}
        onKeyDown={handleOverlayKeyDown}
        aria-label="Close popup overlay"
      />

      <div
        className="animate-in fade-in zoom-in fixed left-1/2 top-1/2 z-50 w-[90%] max-w-lg -translate-x-1/2 -translate-y-1/2 transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-title"
        aria-describedby="newsletter-description"
      >
        <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 transition-all duration-300 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Close popup"
            type="button"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-700" />
          </button>

          <div className="p-8 md:p-10">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <svg className="h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <h3
              id="newsletter-title"
              className="urbanist mb-3 text-center text-2xl font-bold text-gray-900 md:text-3xl"
            >
              {t("newsletter.title")}
            </h3>
            <p id="newsletter-description" className="inter mb-6 text-center text-gray-600">
              {t("newsletter.description")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsletter.placeholder")}
                  required
                  disabled={isSubmitting}
                  className="inter w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              </div>

              {/* Status Messages */}
              {submitStatus.type && (
                <div
                  className={`rounded-lg p-3 text-center text-sm ${
                    submitStatus.type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-primary px-6 py-4 font-urbanist font-semibold text-white transition-all duration-300 hover:bg-primary-dark hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-primary disabled:active:scale-100"
              >
                {isSubmitting ? t("newsletter.submitting") : t("newsletter.subscribe")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScrollNewsletterPopup;
